import React from "react"
import { LinkProps, Box, Button, ButtonProps } from "@carrotfertility/carotene-core"
import { reportErrorMessage } from "#/utils/ErrorReporting"
import { ContentfulArrowLink } from "../shared/contentful-arrow-link"
import { Link as ReactRouterLink } from "react-router-dom"
import { isRelativeLink } from "#/services/markdown/elements"
import { useButtonClickTracking } from "#/services/analytics/useButtonClickTracking"

type ArrowLinkProps = Omit<LinkProps, "component" | "href"> & {
  component?: "a"
  href: string
  title?: string
  "data-noalerts"?: boolean
} & Pick<React.AnchorHTMLAttributes<HTMLAnchorElement>, "target" | "rel">

export function ArrowLink({ children, href, title, ...otherProps }: ArrowLinkProps) {
  if (!href && !otherProps["data-noalerts"]) {
    reportErrorMessage(`Empty href found in Markdown with title ${title} and children ${JSON.stringify(children)}`)
  }
  const linkProps = isRelativeLink(href)
    ? ({ target: "_self", component: ReactRouterLink, to: href, title } as LinkProps<ReactRouterLink>)
    : ({ target: "_blank", href, title } as LinkProps<"a">)

  return (
    <Box>
      <ContentfulArrowLink {...{ ...otherProps, ...linkProps }}>{children}</ContentfulArrowLink>
    </Box>
  )
}

type ButtonLinkProps = {
  children?: React.ReactNode
  href: string
  title?: string
  name?: string
}

type ButtonLinkBaseProps = ButtonLinkProps & Pick<ButtonProps, "variant" | "color">

type ButtonRoutingProps = {
  href?: string
  onClick?: React.MouseEventHandler<HTMLElement>
  target?: string
  component?: React.ElementType
  to?: string
  title?: string
}

function ButtonLinkBase({ children, href, title, name, variant, color }: ButtonLinkBaseProps) {
  const { trackButtonClick } = useButtonClickTracking()

  if (!href) {
    reportErrorMessage(`Empty href found in Markdown ButtonLink with title ${title}`)
  }

  const buttonProps: ButtonRoutingProps = isRelativeLink(href)
    ? { component: ReactRouterLink, to: href, target: "_self", title }
    : { href, target: "_blank", title }

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    trackButtonClick({ name: name ?? title ?? href, href })
    buttonProps.onClick?.(event)
  }

  return (
    <Box
      sx={(theme) => ({
        marginBlockStart: theme.spacing(theme.tokens.spacing.xs),
        marginBlockEnd: theme.spacing(theme.tokens.spacing.lg),
        ":last-child": { marginBlockEnd: 0 }
      })}
    >
      <Button variant={variant} color={color} {...buttonProps} data-id={name ?? undefined} onClick={handleClick}>
        {children}
      </Button>
    </Box>
  )
}

export function PrimaryButton(props: ButtonLinkProps) {
  return <ButtonLinkBase {...props} />
}

export function SecondaryButton(props: ButtonLinkProps) {
  return <ButtonLinkBase {...props} variant="outlined" color="secondary" />
}
